import React from 'react';
import { Button, Modal, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import {
  HistoryEntry,
  HistoryStep,
  HistoryTarget,
  STATUS_COLORS,
  STEP_LABELS,
  timeAgo,
} from './statusVisuals';
import { formatDateTime, formatDateTimeCompact } from '../../utils/datetime';

const { Text } = Typography;

export interface HistoryModalData {
  title: string;
  target: string;
  history: HistoryEntry[];
}

// historyRows turns the raw list into what an operator reads: newest first,
// with each entry marked when it differs from the one before it, so the moments
// the target actually changed state stand out from the steady stretches.
interface HistoryRow extends HistoryEntry {
  key: number;
  changedFrom?: string;
}

function historyRows(history: HistoryEntry[]): HistoryRow[] {
  const asc = [...(history || [])].sort((a, b) => a.timestamp - b.timestamp);
  const rows: HistoryRow[] = asc.map((e, i) => ({
    ...e,
    key: e.timestamp * 1000 + i,
    changedFrom: i > 0 && asc[i - 1].status !== e.status ? asc[i - 1].status : undefined,
  }));
  return rows.reverse();
}

const targetColumns = [
  {
    title: 'Target', dataIndex: 'url', ellipsis: true,
    render: (u: string) => (
      <Tooltip title={u} placement="topLeft"><Text code style={{ fontSize: 12 }}>{u}</Text></Tooltip>
    ),
  },
  {
    title: 'Result', dataIndex: 'success', width: 64,
    // A skipped target was never attempted: the worker's own direct probe had
    // that URL down. It is not a failure of this proxy and must not be drawn
    // as one.
    render: (ok: boolean, t: HistoryTarget) => t.skipped
      ? <Tag style={{ margin: 0, fontSize: 12 }}>skipped</Tag>
      : ok
        ? <Text type="success"><CheckCircleOutlined aria-label="passed" /></Text>
        : <Text type="danger"><CloseCircleOutlined aria-label="failed" /></Text>,
  },
  {
    // Same distinction the live view draws: a target that answered with the
    // expected body is not the same as one that merely answered.
    title: 'Content', key: 'content', width: 90,
    render: (_: unknown, t: HistoryTarget) => t.contentValidated
      ? <Tag color="green" style={{ margin: 0, fontSize: 12 }}>Validated</Tag>
      : <Text type="secondary">—</Text>,
  },
  {
    title: 'Latency', dataIndex: 'latency', width: 76, align: 'right' as const,
    render: (l: number) => l ? `${l.toFixed(0)} ms` : <Text type="secondary">—</Text>,
  },
  {
    title: 'Error', dataIndex: 'error', ellipsis: true,
    render: (e: string | undefined, t: HistoryTarget) => e
      ? <Tooltip title={e} placement="topLeft"><Text type={t.skipped ? 'secondary' : 'danger'} style={{ fontSize: 12 }}>{e}</Text></Tooltip>
      : <Text type="secondary">—</Text>,
  },
];

const stepColumns = [
  {
    title: 'Step', dataIndex: 'name', width: 170,
    render: (n: string, s: HistoryStep) => (
      <Space size={4}>
        <span style={{ fontSize: 12 }}>{STEP_LABELS[n] || n}</span>
        {s.targets && s.targets.length > 0 && (
          <Tag color="volcano" style={{ margin: 0, fontSize: 12 }}>
            {s.targets.length} target{s.targets.length === 1 ? '' : 's'}
          </Tag>
        )}
      </Space>
    ),
  },
  {
    title: 'Result', dataIndex: 'success', width: 64,
    render: (ok: boolean) => ok
      ? <Text type="success"><CheckCircleOutlined aria-label="passed" /></Text>
      : <Text type="danger"><CloseCircleOutlined aria-label="failed" /></Text>,
  },
  {
    title: 'Latency', dataIndex: 'latency', width: 80, align: 'right' as const,
    render: (l: number) => `${(l || 0).toFixed(0)} ms`,
  },
  {
    title: 'Error', dataIndex: 'error', ellipsis: true,
    render: (e?: string) => e
      ? <Tooltip title={e} placement="topLeft"><Text type="danger" style={{ fontSize: 12 }}>{e}</Text></Tooltip>
      : <Text type="secondary">—</Text>,
  },
];

// Full history behind a sparkline: the same records the bars are drawn from,
// with their actual timestamps. State changes are called out — those are the
// moments worth correlating with anything else.
const CheckHistoryModal: React.FC<{
  data: HistoryModalData | null;
  onClose: () => void;
  onOpenCheckLog: (target: string) => void;
}> = ({ data, onClose, onOpenCheckLog }) => (
  <Modal
    open={!!data}
    onCancel={onClose}
    footer={null}
    width={760}
    title={data ? `Check history — ${data.title}` : ''}
  >
    {data && (() => {
      const rows = historyRows(data.history);
      const changes = rows.filter((r) => r.changedFrom).length;
      return (
        <>
          <Space style={{ marginBottom: 8 }} size={8} wrap>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {rows.length} checks kept{rows.length > 0
                ? `, oldest ${formatDateTime(rows[rows.length - 1].timestamp)}`
                : ''} · {changes} state change{changes === 1 ? '' : 's'}
            </Text>
            <Button size="small" onClick={() => onOpenCheckLog(data.target)}>Open check log</Button>
          </Space>
          {/* 10 rows a page: the modal must fit an 800px-tall screen without the
              page itself scrolling behind it. */}
          <Table<HistoryRow>
            dataSource={rows}
            rowKey="key"
            size="small"
            pagination={{
              defaultPageSize: 10,
              size: 'small',
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            columns={[
              {
                title: 'When', dataIndex: 'timestamp', width: 130,
                render: (t: number) => (
                  <Tooltip title={formatDateTime(t)}>
                    <Text style={{ fontSize: 12 }}>{formatDateTimeCompact(t)}</Text>
                  </Tooltip>
                ),
              },
              {
                title: 'Ago', dataIndex: 'timestamp', key: 'ago', width: 84,
                render: (t: number) => (
                  <Text type="secondary" style={{ fontSize: 12 }}>{timeAgo(new Date(t * 1000).toISOString())}</Text>
                ),
              },
              {
                title: 'State', dataIndex: 'status', width: 150,
                render: (s: string, r: HistoryRow) => (
                  <Space size={4}>
                    <Tag color={STATUS_COLORS[s] || 'default'} style={{ margin: 0, fontSize: 12 }}>{s}</Tag>
                    {r.changedFrom && (
                      <Text type="warning" style={{ fontSize: 12 }}>from {r.changedFrom}</Text>
                    )}
                  </Space>
                ),
              },
              {
                title: 'Latency', dataIndex: 'latency',
                align: 'right' as const,
                render: (l?: number) => l ? `${l.toFixed(0)} ms` : <Text type="secondary">—</Text>,
              },
            ]}
            expandable={{
              rowExpandable: (r: HistoryRow) => !!(r.steps && r.steps.length > 0),
              expandedRowRender: (r: HistoryRow) => (
                <Table<HistoryStep>
                  dataSource={r.steps}
                  rowKey="name"
                  size="small"
                  pagination={false}
                  columns={stepColumns}
                  // A step expands once more, into the targets it tried. On a
                  // failure, "all 3 targets failed" is not a diagnosis — one
                  // address refusing the connection and another answering with
                  // the wrong content are different problems. On a success it
                  // answers the other question: which of the configured
                  // addresses actually replied.
                  expandable={{
                    rowExpandable: (s: HistoryStep) => !!(s.targets && s.targets.length > 0),
                    expandedRowRender: (s: HistoryStep) => (
                      <Table<HistoryTarget>
                        dataSource={s.targets}
                        rowKey="url"
                        size="small"
                        pagination={false}
                        columns={targetColumns}
                      />
                    ),
                  }}
                />
              ),
            }}
          />
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
            Up to 100 checks are kept per target, expiring after 7 days — {rows.length} here.
            Expand a row for its step breakdown, and a step again for the target URLs it
            tried — on a failure, how each one ended; on a success, which one answered.
            Older checks were recorded with less detail, so their breakdown can be blank or
            have no target list; the worker and trigger for each check live in the check log.
          </Text>
        </>
      );
    })()}
  </Modal>
);

export default CheckHistoryModal;
